import React, { useState } from 'react';
import {
    Plus,
    Minus,
    Trash2,
    Warehouse,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CartItem } from '@/contexts/CartContext';
import { ImageManager } from '@/lib/imageManager';
import { GroupBuyTooltipBadge } from '@/components/groupbuy/GroupBuyTooltipBadge';
import { LiquidationBadge } from '@/components/liquidation/LiquidationBadge';
import { useNavigate } from 'react-router-dom';
import ProductGradeBadge from '@/components/ProductGradeBadge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface CartItemDisplayProps {
    item: CartItem;
    isSelected: boolean;
    onToggleSelection: (id: string) => void;
    onRemove: (id: string) => void;
    onIncrement: (id: string) => void;
    onDecrement: (id: string) => void;
    closeCart?: () => void;
    isSidebar?: boolean;
}

export const CartItemDisplay: React.FC<CartItemDisplayProps> = ({
    item,
    isSelected,
    onToggleSelection,
    onRemove,
    onIncrement,
    onDecrement,
    closeCart,
    isSidebar = false,
}) => {
    const navigate = useNavigate();
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(price);
    };

    const handleProductClick = () => {
        if (closeCart) closeCart();
        navigate(`/product/${item.product.slug || item.product.id}`);
    };

    return (
        <Card className={isSidebar ? "" : "overflow-hidden"}>
            <CardContent className={isSidebar ? "p-4" : "p-3 sm:p-6"}>
                <div className="flex items-start gap-4" dir="rtl">
                    <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelection(item.id)}
                    />

                    <div
                        className={`relative ${isSidebar ? "w-16 h-16" : "w-16 h-16 sm:w-24 sm:h-24"} bg-muted rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity`}
                        onClick={handleProductClick}
                    >
                        <img
                            src={ImageManager.getImageUrl(item.product.image)}
                            alt={item.product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => ImageManager.handleImageError(e)}
                        />
                    </div>

                    <div className="flex-1 min-w-0 space-y-3">
                        <div className="flex justify-between items-start gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                {item.product.grade && (
                                    <ProductGradeBadge grade={item.product.grade} size="sm" className="flex-shrink-0" />
                                )}
                                <h3
                                    className={`font-semibold ${isSidebar ? "text-base" : "text-base sm:text-lg"} line-clamp-2 cursor-pointer hover:text-lime transition-colors`}
                                    onClick={handleProductClick}
                                >
                                    {item.product.name}
                                </h3>
                            </div>

                            <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent dir="rtl" className="z-[10000]">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="text-right">הסרת מוצר מהעגלה</AlertDialogTitle>
                                        <AlertDialogDescription className="text-right">
                                            האם אתה בטוח שברצונך להסיר את המוצר "{item.product.name}" מהעגלה?
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="flex gap-2 justify-start">
                                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => {
                                                onRemove(item.id);
                                                setShowRemoveConfirm(false);
                                            }}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                            הסר מוצר
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>

                        {item.product.group_buy_enabled && (
                            <GroupBuyTooltipBadge size="sm" />
                        )}

                        {!item.product.group_buy_enabled && item.product.liquidation_sale_enabled && (
                            <LiquidationBadge />
                        )}

                        {!item.product.group_buy_enabled && !item.product.liquidation_sale_enabled && (
                            <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-none shadow-sm text-xs w-fit">
                                מכירה רגילה
                            </Badge>
                        )}


                        <div className="space-y-2">
                            {item.isPackageProduct && item.packageCoverage ? (
                                /* Package product - Unified Green Box Display */
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">כמות:</span>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className={isSidebar ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
                                                onClick={() => {
                                                    // Check against MOQ (which is now packages)
                                                    const minQty = item.moq || 1;
                                                    if (item.quantity <= minQty) {
                                                        setShowRemoveConfirm(true);
                                                    } else {
                                                        onDecrement(item.id);
                                                    }
                                                }}
                                            // disabled prop removed to allow "decrement to remove" pattern
                                            >
                                                <Minus className={isSidebar ? "h-3 w-3" : "h-4 w-4"} />
                                            </Button>
                                            <span className="w-12 text-center font-medium text-sm">
                                                {item.quantity}
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className={isSidebar ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
                                                onClick={() => onIncrement(item.id)}
                                            >
                                                <Plus className={isSidebar ? "h-3 w-3" : "h-4 w-4"} />
                                            </Button>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            חבילות
                                        </span>
                                    </div>

                                    <div className="bg-lime/10 rounded p-2 text-xs space-y-1 mt-2">
                                        <div className="flex justify-start gap-1 items-center w-full">
                                            <span>תקבל בפועל:</span>
                                            <span className="font-bold text-lime">
                                                {(item.quantity * item.packageCoverage).toFixed(2)} {item.measurementUnit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'}
                                            </span>
                                        </div>
                                        <div className="text-muted-foreground">
                                            בחבילה: {item.packageCoverage} {item.measurementUnit === 'package_by_square_meter' ? 'מ"ר' : 'מטר'} | {formatPrice(item.unitPrice)} לחבילה
                                        </div>
                                        <div className="text-muted-foreground">
                                            מינ': {item.moq} חבילות
                                            {item.maxOrderQuantity && (
                                                <span> | מקס': {item.maxOrderQuantity} חבילות</span>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* Non-package product */
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-muted-foreground">כמות:</span>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className={isSidebar ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
                                                onClick={() => {
                                                    if (item.quantity <= item.moq) {
                                                        setShowRemoveConfirm(true);
                                                    } else {
                                                        onDecrement(item.id);
                                                    }
                                                }}
                                            // disabled prop removed to allow "decrement to remove" pattern
                                            >
                                                <Minus className={isSidebar ? "h-3 w-3" : "h-4 w-4"} />
                                            </Button>
                                            <span className="w-12 text-center font-medium text-sm">
                                                {item.quantity}
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className={isSidebar ? "h-7 w-7 p-0" : "h-8 w-8 p-0"}
                                                onClick={() => onIncrement(item.id)}
                                            >
                                                <Plus className={isSidebar ? "h-3 w-3" : "h-4 w-4"} />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="bg-lime/10 rounded p-2 text-xs space-y-1 mt-2">
                                        <div className="flex justify-start gap-1 items-center w-full">
                                            <span>תקבל בפועל:</span>
                                            <span className="font-bold text-lime">
                                                {item.quantity} {item.measurementUnit === 'square_meter' ? 'מ"ר' : item.measurementUnit === 'meter' ? 'מטר' : 'יח\''}
                                            </span>
                                        </div>
                                        <div className="text-muted-foreground">
                                            מינ': {item.moq} {item.measurementUnit === 'square_meter' ? 'מ"ר' : item.measurementUnit === 'meter' ? 'מטר' : 'יח\''}
                                            {item.maxOrderQuantity && (
                                                <span> | מקס': {item.maxOrderQuantity} {item.measurementUnit === 'square_meter' ? 'מ"ר' : item.measurementUnit === 'meter' ? 'מטר' : 'יח\''}</span>
                                            )}
                                            {item.stepQuantity && item.stepQuantity > 1 && (
                                                <span> | קפיצות: {item.stepQuantity} {item.measurementUnit === 'square_meter' ? 'מ"ר' : item.measurementUnit === 'meter' ? 'מטר' : 'יח\''}</span>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="flex justify-between items-end">
                                {!item.isPackageProduct && (
                                    <div className={`text-xs ${isSidebar ? "" : "sm:text-sm"} text-muted-foreground`}>
                                        {formatPrice(item.unitPrice)} ל{item.measurementUnit === 'square_meter' ? 'מ"ר' : item.measurementUnit === 'meter' ? 'מטר' : 'יח\''}
                                    </div>
                                )}
                                <div className={`font-bold ${isSidebar ? "text-base" : "text-base sm:text-lg"} ${item.isPackageProduct ? 'w-full text-left' : ''}`}>
                                    {formatPrice(item.unitPrice * item.quantity)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
